/**
 * SaveSearchModal - Modal for saving property search criteria
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Heart, Bell, Mail, Loader2, Check, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SaveSearchModal({ 
  isOpen, 
  onClose, 
  filters, 
  category = 'property',
  country = 'LK',
  resultCount = 0
}) {
  const [searchName, setSearchName] = useState('');
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifyPush, setNotifyPush] = useState(false);
  const [notifyFrequency, setNotifyFrequency] = useState('instant');
  const [email, setEmail] = useState(localStorage.getItem('userEmail') || '');
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  
  const handleSave = async () => {
    if (!searchName.trim()) {
      toast.error('Please enter a name for your search');
      return;
    }
    
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    
    setIsSaving(true);
    
    try {
      const response = await fetch(`${API_URL}/api/property-saved-searches/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: searchName,
          category,
          filters,
          user_email: email,
          notify_email: notifyEmail,
          notify_push: notifyPush,
          notify_frequency: notifyFrequency,
          country
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to save search');
      }
      
      setSaved(true);
      toast.success('Search saved! You\'ll be notified of new matches.');
      
      // Store email for future use
      localStorage.setItem('userEmail', email);
      
      // Close after short delay
      setTimeout(() => {
        onClose();
        setSaved(false);
        setSearchName('');
      }, 1500);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSaving(false);
    }
  };
  
  // Generate default search name from filters
  const generateSearchName = () => {
    const parts = [];
    
    if (filters.location) {
      parts.push(filters.location);
    }
    
    if (filters.bedrooms && filters.bedrooms !== 'any') {
      parts.push(`${filters.bedrooms} bed`);
    }
    
    if (filters.propertyType && filters.propertyType !== 'any') {
      parts.push(filters.propertyType);
    }
    
    if (filters.maxPrice) {
      const currency = country === 'UK' ? '£' : 'Rs.';
      parts.push(`under ${currency}${filters.maxPrice.toLocaleString()}`);
    }
    
    return parts.length > 0 ? parts.join(' - ') : 'My Property Search';
  };
  
  // Auto-fill search name on open
  React.useEffect(() => {
    if (isOpen && !searchName) {
      setSearchName(generateSearchName());
    }
  }, [isOpen]);
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="save-search-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-pink-500" />
            Save This Search
          </DialogTitle>
          <DialogDescription>
            Get notified when new properties match your criteria
          </DialogDescription>
        </DialogHeader>
        
        {saved ? (
          <div className="py-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Search Saved!</h3>
            <p className="text-gray-600">You'll receive alerts for new matches.</p>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            {/* Search Name */}
            <div className="space-y-2">
              <Label htmlFor="search-name">Search Name</Label>
              <Input
                id="search-name"
                placeholder="e.g., 2 bed flat in London"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                data-testid="search-name-input"
              />
            </div>
            
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email for notifications</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="email-input"
              />
            </div>
            
            {/* Notification Options */}
            <div className="space-y-3 pt-2">
              <Label className="text-sm font-medium">Notification Preferences</Label>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span className="text-sm">Email notifications</span>
                </div>
                <Switch
                  checked={notifyEmail}
                  onCheckedChange={setNotifyEmail}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4 text-gray-500" />
                  <span className="text-sm">Push notifications</span>
                </div>
                <Switch
                  checked={notifyPush}
                  onCheckedChange={setNotifyPush}
                />
              </div>
              
              {/* Notification Frequency */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium">Notification Frequency</span>
                </div>
                <Select value={notifyFrequency} onValueChange={setNotifyFrequency}>
                  <SelectTrigger className="w-full bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="instant">Instant - As soon as new listings are posted</SelectItem>
                    <SelectItem value="daily">Daily Digest - One summary email per day</SelectItem>
                    <SelectItem value="both">Both - Instant notifications + Daily summary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Current Filters Summary */}
            <div className="text-sm text-gray-500 bg-blue-50 p-3 rounded-lg">
              <p className="font-medium text-blue-800 mb-1">Current Search:</p>
              <p>
                {resultCount.toLocaleString()} properties match your criteria
              </p>
            </div>
            
            {/* Link to manage saved searches */}
            <div className="text-center">
              <Link 
                to="/my-saved-searches" 
                className="text-sm text-blue-600 hover:underline"
                onClick={onClose}
              >
                Manage all saved searches →
              </Link>
            </div>
          </div>
        )}
        
        {!saved && (
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={isSaving}
              className="bg-pink-500 hover:bg-pink-600"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Heart className="w-4 h-4 mr-2" />
                  Save Search
                </>
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
